import BadgeIcon from '@mui/icons-material/Badge'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import UploadIcon from '@mui/icons-material/Upload'
import WomanIcon from '@mui/icons-material/Woman'
import ChildCareIcon from '@mui/icons-material/ChildCare'
import FaceIcon from '@mui/icons-material/Face'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  ClickAwayListener,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import Webcam from 'react-webcam'
import {
  getSmartQueueSearch,
  sendSmartQueueOtp,
  submitSmartQueue,
} from '../../services/SmartQueueService'
import { makeStyles } from '../../utils/hooks/makeStyles'

// Department options
const DEPARTMENT_OPTIONS = [
  { label: 'Cardiology', value: 'cardiology' },
  { label: 'Neurology', value: 'neurology' },
  { label: 'Oncology', value: 'oncology' },
  { label: 'Pediatrics', value: 'pediatrics' },
  { label: 'Orthopedics', value: 'orthopedics' },
  { label: 'Gynecology', value: 'gynecology' },
  { label: 'Emergency', value: 'emergency' },
  { label: 'Radiology', value: 'radiology' },
  { label: 'Gastroenterology', value: 'gastroenterology' },
  { label: 'Urology', value: 'urology' },
  { label: 'Nephrology', value: 'nephrology' },
  { label: 'Dermatology', value: 'dermatology' },
  { label: 'ENT', value: 'ent' },
  { label: 'Endocrinology', value: 'endocrinology' },
  { label: 'Pulmonology', value: 'pulmonology' },
  { label: 'Psychiatry', value: 'psychiatry' },
  { label: 'Ophthalmology', value: 'ophthalmology' },
  { label: 'Hematology', value: 'hematology' },
  { label: 'Rheumatology', value: 'rheumatology' },
  { label: 'General Surgery', value: 'general_surgery' },
  { label: 'Plastic Surgery', value: 'plastic_surgery' },
  { label: 'Vascular Surgery', value: 'vascular_surgery' },
  { label: 'Anesthesiology', value: 'anesthesiology' },
  { label: 'Pathology', value: 'pathology' },
  { label: 'Physiotherapy', value: 'physiotherapy' },
  { label: 'Nutrition and Dietetics', value: 'nutrition_dietetics' },
  { label: 'General Medicine', value: 'general_medicine' },
]

// Styles using project's makeStyles
const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1400,
    margin: '0 auto',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  pageTitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
      marginBottom: theme.spacing(2),
    },
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  sectionTitle: {
    marginBottom: theme.spacing(2.5),
    color: '#333',
    fontWeight: 600,
    fontSize: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.1rem',
      marginBottom: theme.spacing(2),
    },
  },
  formRow: {
    marginBottom: theme.spacing(2),
  },
  formLabel: {
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    color: '#555',
    fontSize: '0.95rem',
  },
  checkboxGroup: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    },
  },
  actions: {
    marginTop: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
  },
  buttonPrimary: {
    textTransform: 'none',
    fontWeight: 500,
    padding: theme.spacing(1.2, 3),
    minWidth: 140,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minWidth: 'unset',
    },
  },
  buttonSuccess: {
    textTransform: 'none',
    fontWeight: 500,
    padding: theme.spacing(1.2, 3),
    minWidth: 140,
    backgroundColor: '#4caf50',
    color: '#fff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      backgroundColor: '#45a049',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minWidth: 'unset',
    },
  },
  otpField: {
    flex: 1,
    minWidth: 200,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minWidth: 'unset',
    },
  },
  tokenBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2.5),
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: theme.spacing(1),
    color: '#fff',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
  },
  tokenTitle: {
    fontWeight: 600,
    fontSize: '1.1rem',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tokenInfo: {
    fontSize: '1.25rem',
    fontWeight: 500,
    letterSpacing: 0.5,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.1rem',
    },
  },
  searchContainer: {
    marginBottom: theme.spacing(2),
  },
  searchBox: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  searchField: {
    flex: 1,
    minWidth: 300,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'unset',
      width: '100%',
    },
  },
  searchButton: {
    minHeight: 40,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  searchResults: {
    marginTop: theme.spacing(2),
  },
  resultCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: '1px solid #e0e0e0',
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
    },
  },
  resultRow: {
    display: 'flex',
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      marginBottom: theme.spacing(1),
    },
  },
  resultLabel: {
    fontWeight: 600,
    // minWidth: 150,

    color: '#666',
    [theme.breakpoints.down('sm')]: {
      minWidth: 'unset',
      marginBottom: theme.spacing(0.25),
    },
  },
  resultValue: {
    color: '#333',
    paddingLeft: theme.spacing(1),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#999',
    fontSize: '1rem',
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  searchDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 1000,
    maxHeight: 300,
    overflow: 'auto',
  },
  searchDropdownItem: {
    padding: theme.spacing(1.5, 2),
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  searchDropdownIcon: {
    color: '#666',
    fontSize: '1.2rem',
  },
  searchDropdownText: {
    display: 'flex',
    flexDirection: 'column',
  },
  searchDropdownLabel: {
    fontSize: '0.95rem',
    color: '#333',
  },
  searchDropdownQuery: {
    fontWeight: 600,
    color: '#1976d2',
  },
  searchInputWrapper: {
    position: 'relative',
    flex: 1,
  },
  cameraBox: {
    width: '100%',
    height: 250,
    border: '2px dashed #ccc',
    borderRadius: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f9f9f9',
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: '#1976d2',
      backgroundColor: '#f0f7ff',
    },
  },
  cameraIcon: {
    fontSize: '4rem',
    color: '#999',
    marginBottom: theme.spacing(1),
  },
  webcamContainer: {
    width: '100%',
    maxWidth: 500,
    margin: '0 auto',
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    backgroundColor: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& video': {
      width: '100%',
      height: 'auto',
      maxHeight: 400,
      objectFit: 'contain',
      borderRadius: theme.spacing(1),
    },
  },
  capturedImage: {
    width: '100%',
    maxWidth: 500,
    height: 'auto',
    maxHeight: 400,
    objectFit: 'contain',
    borderRadius: theme.spacing(1),
    backgroundColor: '#000',
    display: 'block',
    margin: '0 auto',
    marginBottom: theme.spacing(2),
  },
  cameraButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    maxWidth: 500,
    margin: '0 auto',
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  photoSectionWrapper: {
    maxWidth: 500,
    margin: '0 auto',
  },
  patientInfoBox: {
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  patientInfoRow: {
    display: 'flex',
    marginBottom: theme.spacing(1),
  },
  patientInfoLabel: {
    fontWeight: 600,
    minWidth: 120,
    color: '#666',
  },
  patientInfoValue: {
    color: '#333',
  },
  visitorSelectionBox: {
    padding: theme.spacing(3),
    backgroundColor: '#f9f9f9',
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  visitorOptionsContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  visitorOption: {
    flex: 1,
    padding: theme.spacing(3),
    border: '2px solid #e0e0e0',
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: '#fff',
    '&:hover': {
      borderColor: '#1976d2',
      backgroundColor: '#f0f7ff',
      transform: 'translateY(-4px)',
      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
    },
  },
  visitorOptionSelected: {
    borderColor: '#1976d2',
    backgroundColor: '#e3f2fd',
    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
    '&:hover': {
      backgroundColor: '#e3f2fd',
    },
  },
  visitorIcon: {
    fontSize: '3rem',
    color: '#666',
  },
  visitorIconSelected: {
    color: '#1976d2',
  },
  visitorLabel: {
    fontWeight: 600,
    fontSize: '1rem',
    color: '#333',
  },
  visitorLabelSelected: {
    color: '#1976d2',
  },
}))

type VisitPurpose = 'Appointment' | 'Lab test' | 'Pharmacy' | ''

type VisitorRelationship = 'self' | 'wife' | 'child' | ''

type SearchType = 'token' | 'mobile' | 'name' | ''

type Entry = {
  id: string
  name: string
  mobile: string
  department: string
  visitPurpose: VisitPurpose
  appointmentType?: 'Emergency' | 'Normal' | ''
  token: string
  queuePosition: number
  estimatedWaitingTime: number
}

type TokenInfo = {
  token: string
  queuePosition: number
  estimatedWaitingTime: number
  department: string
  visitPurpose?: VisitPurpose
}

type RecognizedPatient = {
  id: string
  name: string
  mobile: string
  appointmentDate?: string
  appointmentTime?: string
  doctor?: string
  department?: string
  registrationDate?: string
}

type QueueEntry = {
  id?: string
  name: string
  mobile: string
  department: string
  visitPurpose: VisitPurpose
  appointmentType?: 'Emergency' | 'Normal' | ''
  token: string
  queuePosition: number
  estimatedWaitingTime: number
}

// Flow types based on photo recognition
type PatientFlow = 'new' | 'with-appointment' | 'without-appointment' | null

type RecognitionProgress = {
  stage: 'capturing' | 'analyzing' | 'complete' | null
  message: string
}

const SmartQueueSystem: React.FC = () => {
  const classes = useStyles()

  // Main photo capture state
  const [showWebcam, setShowWebcam] = useState(false)
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null)
  const webcamRef = useRef<Webcam>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Recognition and flow determination
  const [detectedFlow, setDetectedFlow] = useState<PatientFlow>(null)
  const [selectedDemoFlow, setSelectedDemoFlow] = useState<PatientFlow>(null) // For demo flow selection
  const [flowSelectionComplete, setFlowSelectionComplete] = useState(false) // Track if flow is selected
  const [recognitionProgress, setRecognitionProgress] =
    useState<RecognitionProgress>({ stage: null, message: '' })
  const [recognizedPatient, setRecognizedPatient] =
    useState<RecognizedPatient | null>(null)

  // Flow 1: New Patient Registration
  const [name, setName] = useState('')
  const [mobile, setMobile] = useState('')
  const [department, setDepartment] = useState('')
  const [visitPurpose, setVisitPurpose] = useState<VisitPurpose>('')
  const [isEmergency, setIsEmergency] = useState(false)
  const [isNormal, setIsNormal] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [enteredOtp, setEnteredOtp] = useState('')
  const [entries, setEntries] = useState<QueueEntry | null>(null)

  // Flow 2: Patient with Appointment
  const [generatedToken1, setGeneratedToken1] = useState<TokenInfo | null>(null)

  // Flow 3: Patient without Appointment (Walk-in)
  const [selectedVisitorRelationship, setSelectedVisitorRelationship] =
    useState<VisitorRelationship>('')
  const [selectedDepartment, setSelectedDepartment] = useState('')
  const [selectedVisitPurpose, setSelectedVisitPurpose] =
    useState<VisitPurpose>('')
  const [generatedToken2, setGeneratedToken2] = useState<TokenInfo | null>(null)

  // Search functionality (kept for reference)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchType, setSearchType] = useState<SearchType>('')
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)
  const searchInputRef = useRef<HTMLDivElement>(null)
  const [results, setResults] = useState<Entry[]>([])

  // Camera permission state
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)

  // Validation helpers
  const validateMobile = (m: string) => /^\d{10}$/.test(m)

  // commented for future use
  // const [showSuccess, setShowSuccess] = useState(false)
  // const [otp, setOtp] = useState('')

  const canSendOtp = React.useMemo(() => {
    return (
      validateMobile(mobile) &&
      name?.trim()?.length > 0 &&
      department &&
      visitPurpose &&
      (visitPurpose !== 'Appointment' || isEmergency || isNormal)
    )
  }, [mobile, name, department, visitPurpose, isEmergency, isNormal])

  const handleSearchQueryChange = (value: string) => {
    setSearchQuery(value)
    setSearchType('')
    if (value?.trim()) {
      setShowSearchDropdown(true)
    } else {
      setShowSearchDropdown(false)
    }
  }

  const handleSearchTypeSelect = (type: SearchType) => {
    setSearchType(type)
    setShowSearchDropdown(false)
    handleSearchDetails(type)
  }

  const handleClickAway = () => {
    setShowSearchDropdown(false)
  }

  const handleSendOtp = React.useCallback(async () => {
    if (!canSendOtp) return
    try {
      const response = await sendSmartQueueOtp(mobile)
      if (response?.success) {
        toast.success(response?.data)
        setOtpSent(true)
      } else {
        setOtpSent(false)
        toast.error(response?.message)
      }
    } catch (error) {
      setOtpSent(false)
      toast.error('Something went wrong while sending OTP. Please try again.')
    }
  }, [mobile, canSendOtp])

  const handleSaveDetails = React.useCallback(async () => {
    if (!canSendOtp) return
    const requestBody = {
      name: name,
      mobile: mobile,
      visitPurpose: visitPurpose,
      appointmentType:
        visitPurpose === 'Appointment'
          ? isEmergency
            ? 'Emergency'
            : isNormal
            ? 'Normal'
            : ''
          : '',
      department: department,
    }
    try {
      const response = await submitSmartQueue(requestBody, enteredOtp)
      if (response?.success) {
        setEntries(response?.data)
        // setShowSuccess(true)
        // setTimeout(() => setShowSuccess(false), 5000)
        clearData()
      } else {
        toast.error(response?.message)
        clearData()
      }
    } catch (error) {
      toast.error(
        'Something went wrong while submit Details. Please try again.'
      )
      clearData()
    }
  }, [
    mobile,
    canSendOtp,
    enteredOtp,
    name,
    visitPurpose,
    department,
    isEmergency,
    isNormal,
  ])

  const handleSearchDetails = React.useCallback(
    async (type: string) => {
      if (type === '') return
      try {
        const response = await getSmartQueueSearch(type, searchQuery)
        if (response?.success) {
          setResults(response?.data)
        } else {
          setResults([])
          toast.error(response?.message)
        }
      } catch (error) {
        setResults([])
        toast.error(
          'Something went wrong while fetching Details. Please try again.'
        )
      }
    },
    [searchQuery]
  )

  // Request camera permission
  const requestCameraPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      // Permission granted
      setHasPermission(true)
      // Stop the stream immediately as we just needed to check permission
      stream.getTracks().forEach((track) => track.stop())
      return true
    } catch (err) {
      console.error('Camera permission error:', err)
      setHasPermission(false)
      toast.error(
        'Camera access denied. Please allow camera access in your browser settings.'
      )
      return false
    }
  }, [])

  // Check camera permission on component mount
  useEffect(() => {
    const checkPermission = async () => {
      if (
        typeof navigator !== 'undefined' &&
        navigator.mediaDevices &&
        typeof navigator.mediaDevices.getUserMedia !== 'undefined'
      ) {
        try {
          // Check if permission API is available
          if (navigator.permissions) {
            const result = await navigator.permissions.query({
              name: 'camera' as PermissionName,
            })
            setHasPermission(result.state === 'granted')

            // Listen for permission changes
            result.onchange = () => {
              setHasPermission(result.state === 'granted')
            }
          }
        } catch (err) {
          // Permission API not supported, will request on camera open
          console.log('Permission API not supported, will request on use')
        }
      } else {
        toast.error('Camera is not supported on this device/browser.')
        setHasPermission(false)
      }
    }

    checkPermission()
  }, [])

  // Handle opening camera with permission check
  const handleOpenCamera = useCallback(async () => {
    if (hasPermission === false) {
      const granted = await requestCameraPermission()
      if (!granted) return
    }
    setShowWebcam(true)
  }, [hasPermission, requestCameraPermission])

  // Auto-open camera when flow selection is complete
  useEffect(() => {
    if (flowSelectionComplete && !detectedFlow && !capturedPhoto) {
      handleOpenCamera()
    }
  }, [flowSelectionComplete, detectedFlow, capturedPhoto, handleOpenCamera])

  // Photo recognition and flow determination (Demo simulation)
  const processPhotoRecognition = useCallback(
    (photoData: string) => {
      setCapturedPhoto(photoData)
      setShowWebcam(false)

      // Stage 1: Capturing complete
      setRecognitionProgress({
        stage: 'capturing',
        message: '📸 Photo captured successfully!',
      })

      setTimeout(() => {
        // Stage 2: Analyzing
        setRecognitionProgress({
          stage: 'analyzing',
          message: '🔍 Analyzing face and matching with database...',
        })

        setTimeout(() => {
          // Use selected demo flow if available, otherwise random
          let flowToUse: PatientFlow

          if (selectedDemoFlow) {
            // Use the manually selected flow for demo
            flowToUse = selectedDemoFlow
          } else {
            // Randomly simulate different patient scenarios for demo
            const scenarios = [
              'new',
              'with-appointment',
              'without-appointment',
            ] as const
            flowToUse = scenarios[Math.floor(Math.random() * scenarios.length)]
          }

          setDetectedFlow(flowToUse)

          if (flowToUse === 'new') {
            // New patient - not registered
            setRecognitionProgress({
              stage: 'complete',
              message: '✨ New patient detected! Please complete registration.',
            })
            toast.info('New patient detected! Please fill in your details.')
          } else if (flowToUse === 'with-appointment') {
            // Existing patient with appointment
            const mockPatient = {
              id: 'PT12345',
              name: 'John Doe',
              mobile: '9876543210',
              appointmentDate: new Date().toLocaleDateString(),
              appointmentTime: '10:30 AM',
              doctor: 'Dr. Sarah Johnson',
              department: 'cardiology',
            }
            setRecognizedPatient(mockPatient)

            // Auto-generate token
            const mockToken = {
              token: `TK${Math.floor(Math.random() * 9000) + 1000}`,
              queuePosition: Math.floor(Math.random() * 10) + 1,
              estimatedWaitingTime: Math.floor(Math.random() * 30) + 10,
              department: 'cardiology',
            }
            setGeneratedToken1(mockToken)

            setRecognitionProgress({
              stage: 'complete',
              message:
                '✅ Patient recognized with appointment! Token generated.',
            })
            toast.success('Welcome back! Your appointment is confirmed.')
          } else if (flowToUse === 'without-appointment') {
            // Existing patient without appointment (walk-in)
            const mockPatient = {
              id: 'PT67890',
              name: 'Jane Smith',
              mobile: '9123456789',
              registrationDate: new Date().toLocaleDateString(),
            }
            setRecognizedPatient(mockPatient)

            setRecognitionProgress({
              stage: 'complete',
              message: '✅ Patient recognized! Please select visit details.',
            })
            toast.success('Welcome back! Please select your visit details.')
          }
        }, 2000) // Analyzing time
      }, 800) // Capturing time
    },
    [selectedDemoFlow]
  )

  // Capture photo from webcam
  const capturePhoto = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot()
    if (imageSrc) {
      processPhotoRecognition(imageSrc)
    }
  }, [processPhotoRecognition])

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        processPhotoRecognition(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // Handle webcam errors
  const handleWebcamError = useCallback((error: string | DOMException) => {
    console.error('Webcam error:', error)
    setHasPermission(false)
    toast.error(
      'Failed to access camera. Please check camera permissions and try again.'
    )
    setShowWebcam(false)
  }, [])

  // Reset all states and start over
  const handleStartOver = () => {
    setCapturedPhoto(null)
    setShowWebcam(false)
    setDetectedFlow(null)
    setSelectedDemoFlow(null) // Reset demo flow selection
    setFlowSelectionComplete(false) // Reset flow selection step
    setRecognitionProgress({ stage: null, message: '' })
    setRecognizedPatient(null)

    // Reset flow-specific states
    setName('')
    setMobile('')
    setDepartment('')
    setVisitPurpose('')
    setIsEmergency(false)
    setIsNormal(false)
    setOtpSent(false)
    setEnteredOtp('')
    setEntries(null)

    setGeneratedToken1(null)

    setSelectedVisitorRelationship('')
    setSelectedDepartment('')
    setSelectedVisitPurpose('')
    setGeneratedToken2(null)
  }

  // Handle flow selection and proceed to photo capture
  const handleProceedWithFlow = () => {
    // Allow proceeding even if selectedDemoFlow is null (Random option)
    setFlowSelectionComplete(true)
  }

  const clearData = () => {
    setName('')
    setDepartment('')
    setVisitPurpose('')
    setIsEmergency(false)
    setIsNormal(false)
    setOtpSent(false)
    setEnteredOtp('')
    setMobile('')
  }

  // Demo: Generate token for walk-in patients
  const handleGenerateToken2 = () => {
    if (!selectedDepartment || !selectedVisitPurpose) {
      toast.error('Please select department and visit purpose')
      return
    }

    const mockToken = {
      token: `TK${Math.floor(Math.random() * 9000) + 1000}`,
      queuePosition: Math.floor(Math.random() * 15) + 1,
      estimatedWaitingTime: Math.floor(Math.random() * 45) + 15,
      department: selectedDepartment,
      visitPurpose: selectedVisitPurpose,
    }
    setGeneratedToken2(mockToken)
    toast.success('Token generated successfully!')
  }

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 0.3;
              transform: scale(0.8);
            }
            50% {
              opacity: 1;
              transform: scale(1.2);
            }
          }
        `}
      </style>
      <div className={classes.root}>
        <Typography variant='h4' className={classes.pageTitle}>
          Smart Queue Management System
        </Typography>

        {/* Step 1: Flow Selection - Show first if flow not selected */}
        {!flowSelectionComplete && !detectedFlow && (
          <Paper className={classes.section} elevation={3}>
            <Typography
              variant='h6'
              className={classes.sectionTitle}
              style={{ textAlign: 'center' }}
            >
              🎬 Demo Mode: Select Patient Scenario
            </Typography>
            <Typography
              variant='body2'
              style={{ marginBottom: 24, color: '#666', textAlign: 'center' }}
            >
              Choose which patient flow you want to demonstrate
            </Typography>
            <Divider className={classes.divider} />

            <Grid container spacing={3} justifyContent='center'>
              <Grid item xs={12} md={8} lg={6}>
                {/* Flow Selection Cards */}
                <Box
                  style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
                >
                  {/* Option 1: New Patient */}
                  <Card
                    className={
                      selectedDemoFlow === 'new'
                        ? classes.visitorOptionSelected
                        : ''
                    }
                    onClick={() => setSelectedDemoFlow('new')}
                    style={{
                      cursor: 'pointer',
                      border:
                        selectedDemoFlow === 'new'
                          ? '3px solid #1976d2'
                          : '2px solid #e0e0e0',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <CardContent style={{ padding: 24 }}>
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 16,
                        }}
                      >
                        <Box
                          style={{
                            fontSize: '3rem',
                            minWidth: 60,
                            textAlign: 'center',
                          }}
                        >
                          📝
                        </Box>
                        <Box style={{ flex: 1 }}>
                          <Typography
                            variant='h6'
                            style={{ fontWeight: 600, marginBottom: 8 }}
                          >
                            New Patient Registration
                          </Typography>
                          <Typography variant='body2' color='textSecondary'>
                            Patient not found in database. Show registration
                            form with OTP verification and token generation.
                          </Typography>
                        </Box>
                        {selectedDemoFlow === 'new' && (
                          <CheckCircleIcon
                            style={{ color: '#1976d2', fontSize: '2rem' }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Option 2: Patient with Appointment */}
                  <Card
                    className={
                      selectedDemoFlow === 'with-appointment'
                        ? classes.visitorOptionSelected
                        : ''
                    }
                    onClick={() => setSelectedDemoFlow('with-appointment')}
                    style={{
                      cursor: 'pointer',
                      border:
                        selectedDemoFlow === 'with-appointment'
                          ? '3px solid #4caf50'
                          : '2px solid #e0e0e0',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <CardContent style={{ padding: 24 }}>
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 16,
                        }}
                      >
                        <Box
                          style={{
                            fontSize: '3rem',
                            minWidth: 60,
                            textAlign: 'center',
                          }}
                        >
                          ✅
                        </Box>
                        <Box style={{ flex: 1 }}>
                          <Typography
                            variant='h6'
                            style={{ fontWeight: 600, marginBottom: 8 }}
                          >
                            Patient with Appointment
                          </Typography>
                          <Typography variant='body2' color='textSecondary'>
                            Patient recognized with existing appointment.
                            Auto-generate token with appointment details.
                          </Typography>
                        </Box>
                        {selectedDemoFlow === 'with-appointment' && (
                          <CheckCircleIcon
                            style={{ color: '#4caf50', fontSize: '2rem' }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Option 3: Walk-in Patient */}
                  <Card
                    className={
                      selectedDemoFlow === 'without-appointment'
                        ? classes.visitorOptionSelected
                        : ''
                    }
                    onClick={() => setSelectedDemoFlow('without-appointment')}
                    style={{
                      cursor: 'pointer',
                      border:
                        selectedDemoFlow === 'without-appointment'
                          ? '3px solid #ff9800'
                          : '2px solid #e0e0e0',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <CardContent style={{ padding: 24 }}>
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 16,
                        }}
                      >
                        <Box
                          style={{
                            fontSize: '3rem',
                            minWidth: 60,
                            textAlign: 'center',
                          }}
                        >
                          🚶
                        </Box>
                        <Box style={{ flex: 1 }}>
                          <Typography
                            variant='h6'
                            style={{ fontWeight: 600, marginBottom: 8 }}
                          >
                            Walk-in Patient (No Appointment)
                          </Typography>
                          <Typography variant='body2' color='textSecondary'>
                            Patient recognized but no appointment. Select
                            visitor, department, and visit purpose.
                          </Typography>
                        </Box>
                        {selectedDemoFlow === 'without-appointment' && (
                          <CheckCircleIcon
                            style={{ color: '#ff9800', fontSize: '2rem' }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Random Option */}
                  <Card
                    className={
                      !selectedDemoFlow ? classes.visitorOptionSelected : ''
                    }
                    onClick={() => setSelectedDemoFlow(null)}
                    style={{
                      cursor: 'pointer',
                      border: !selectedDemoFlow
                        ? '3px solid #9c27b0'
                        : '2px solid #e0e0e0',
                      transition: 'all 0.3s ease',
                      opacity: 0.85,
                    }}
                  >
                    <CardContent style={{ padding: 24 }}>
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 16,
                        }}
                      >
                        <Box
                          style={{
                            fontSize: '3rem',
                            minWidth: 60,
                            textAlign: 'center',
                          }}
                        >
                          🎲
                        </Box>
                        <Box style={{ flex: 1 }}>
                          <Typography
                            variant='h6'
                            style={{ fontWeight: 600, marginBottom: 8 }}
                          >
                            Random (Auto-detect)
                          </Typography>
                          <Typography variant='body2' color='textSecondary'>
                            System will randomly select one of the above flows
                            for demonstration.
                          </Typography>
                        </Box>
                        {!selectedDemoFlow && (
                          <CheckCircleIcon
                            style={{ color: '#9c27b0', fontSize: '2rem' }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>

                {/* Proceed Button */}
                <Box style={{ marginTop: 32, textAlign: 'center' }}>
                  <Button
                    variant='contained'
                    size='large'
                    onClick={handleProceedWithFlow}
                    style={{
                      minWidth: 200,
                      padding: '12px 48px',
                      fontSize: '1.1rem',
                    }}
                    startIcon={<CameraAltIcon />}
                  >
                    Proceed to Photo Capture
                  </Button>
                  <Typography
                    variant='caption'
                    style={{ display: 'block', marginTop: 12, color: '#666' }}
                  >
                    Click to proceed with{' '}
                    {selectedDemoFlow ? 'selected' : 'random'} flow
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Step 2: Photo Capture Section - Shows after flow selection */}
        {flowSelectionComplete && !detectedFlow && (
          <Paper className={classes.section} elevation={3}>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <Typography
                variant='h6'
                className={classes.sectionTitle}
                style={{ marginBottom: 0 }}
              >
                <CameraAltIcon /> Patient Photo Recognition
              </Typography>
              <Button
                variant='outlined'
                size='small'
                onClick={() => setFlowSelectionComplete(false)}
              >
                ← Back
              </Button>
            </Box>
            <Typography
              variant='body2'
              style={{ marginBottom: 24, color: '#666', textAlign: 'center' }}
            >
              Capture or upload your photo to continue
            </Typography>
            <Divider className={classes.divider} />{' '}
            <Grid container spacing={3} justifyContent='center'>
              <Grid item xs={12} md={8}>
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='image/*'
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />

                {showWebcam && !capturedPhoto && (
                  <Box>
                    <Box className={classes.webcamContainer}>
                      <Webcam
                        ref={webcamRef}
                        audio={false}
                        screenshotFormat='image/jpeg'
                        videoConstraints={{
                          width: 1280,
                          height: 720,
                          facingMode: 'user',
                        }}
                        onUserMediaError={handleWebcamError}
                      />
                    </Box>
                    <Box className={classes.cameraButtons}>
                      <Button
                        variant='contained'
                        onClick={capturePhoto}
                        startIcon={<CameraAltIcon />}
                        fullWidth
                        size='small'
                      >
                        Capture
                      </Button>
                      <Button
                        variant='outlined'
                        startIcon={<UploadIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        fullWidth
                        size='small'
                      >
                        Upload
                      </Button>
                    </Box>
                  </Box>
                )}

                {capturedPhoto && recognitionProgress.stage && (
                  <Box>
                    <img
                      src={capturedPhoto}
                      alt='Captured Patient'
                      className={classes.capturedImage}
                    />

                    {/* Progress indicator */}
                    <Box
                      style={{
                        padding: 24,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 8,
                        marginTop: 16,
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        variant='h6'
                        style={{ color: '#1976d2', marginBottom: 12 }}
                      >
                        {recognitionProgress.message}
                      </Typography>
                      {recognitionProgress.stage === 'analyzing' && (
                        <Box
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 8,
                            marginTop: 16,
                          }}
                        >
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: '#1976d2',
                              animation: 'pulse 1.5s infinite',
                            }}
                          />
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: '#1976d2',
                              animation: 'pulse 1.5s infinite 0.2s',
                            }}
                          />
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: '#1976d2',
                              animation: 'pulse 1.5s infinite 0.4s',
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Flow 1: New Patient Registration - Shows only if new patient detected */}
        {detectedFlow === 'new' && (
          <Paper className={classes.section} elevation={3}>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <Typography variant='h6' className={classes.sectionTitle}>
                📝 New Patient Registration
              </Typography>
              <Button variant='outlined' size='small' onClick={handleStartOver}>
                Start Over
              </Button>
            </Box>

            {/* Show captured photo */}
            {capturedPhoto && (
              <Box style={{ textAlign: 'center', marginBottom: 24 }}>
                <img
                  src={capturedPhoto}
                  alt='Patient'
                  style={{
                    width: 150,
                    height: 150,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '4px solid #1976d2',
                  }}
                />
              </Box>
            )}

            <Divider className={classes.divider} />
            <Grid container spacing={2} className={classes.formRow}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Full Name'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  required
                  variant='outlined'
                  placeholder='Enter patient full name'
                  size='small'
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label='Mobile Number'
                  value={mobile}
                  onChange={(e) =>
                    setMobile(e.target.value.replace(/[^0-9]/g, ''))
                  }
                  fullWidth
                  required
                  variant='outlined'
                  placeholder='10 digit mobile number'
                  inputProps={{ maxLength: 10 }}
                  size='small'
                  error={mobile?.length > 0 && !validateMobile(mobile)}
                  helperText={
                    mobile?.length > 0 && !validateMobile(mobile)
                      ? 'Mobile number must be 10 digits'
                      : ''
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required variant='outlined' size='small'>
                  <InputLabel id='department-label'>Department</InputLabel>
                  <Select
                    labelId='department-label'
                    value={department}
                    label='Department'
                    onChange={(e) => setDepartment(e.target.value)}
                  >
                    <MenuItem value=''>
                      <em>Select Department</em>
                    </MenuItem>
                    {DEPARTMENT_OPTIONS?.map((dept) => (
                      <MenuItem key={dept?.value} value={dept?.value}>
                        {dept?.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required variant='outlined' size='small'>
                  <InputLabel id='visit-purpose-label'>
                    Visit Purpose
                  </InputLabel>
                  <Select
                    labelId='visit-purpose-label'
                    value={visitPurpose}
                    label='Visit Purpose'
                    onChange={(e) =>
                      setVisitPurpose(e.target.value as VisitPurpose)
                    }
                  >
                    <MenuItem value=''>
                      <em>Select Visit Purpose</em>
                    </MenuItem>
                    <MenuItem value='Appointment'>Appointment</MenuItem>
                    <MenuItem value='Lab test'>Lab Test</MenuItem>
                    <MenuItem value='Pharmacy'>Pharmacy</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {visitPurpose === 'Appointment' && (
                <Grid item xs={12}>
                  <Typography variant='body2' className={classes.formLabel}>
                    Appointment Type <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Box className={classes.checkboxGroup}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isEmergency}
                          onChange={(e) => {
                            setIsEmergency(e.target.checked)
                            if (e.target.checked) setIsNormal(false)
                          }}
                          color='error'
                        />
                      }
                      label='Emergency'
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isNormal}
                          onChange={(e) => {
                            setIsNormal(e.target.checked)
                            if (e.target.checked) setIsEmergency(false)
                          }}
                          color='primary'
                        />
                      }
                      label='Normal'
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
            <Divider className={classes.divider} />
            <div className={classes.actions}>
              <Button
                variant='contained'
                onClick={handleSendOtp}
                disabled={!canSendOtp || otpSent}
                className={classes.buttonPrimary}
                size='small'
              >
                {otpSent ? 'OTP Sent' : 'Send OTP'}
              </Button>

              <TextField
                label='Enter OTP'
                value={enteredOtp}
                onChange={(e) =>
                  setEnteredOtp(e.target.value.replace(/[^0-9]/g, ''))
                }
                inputProps={{ maxLength: 6 }}
                disabled={!otpSent}
                className={classes.otpField}
                variant='outlined'
                placeholder='6 digit OTP'
                size='small'
              />

              <Button
                variant='contained'
                onClick={handleSaveDetails}
                disabled={enteredOtp.length < 6}
                className={classes.buttonSuccess}
                size='small'
                startIcon={<CheckCircleIcon />}
              >
                Save Details
              </Button>
            </div>
            {/* // commented for future use
        {showSuccess && (
          <Alert
            severity='success'
            onClose={() => setShowSuccess(false)}
            className={classes.tokenBox}
          >
            Registration successful! Token generated.
          </Alert>
        )} */}

            {entries && (
              <Box className={classes.tokenBox}>
                <Typography variant='subtitle1' className={classes.tokenTitle}>
                  <CheckCircleIcon /> Latest Token Generated
                </Typography>
                <Typography className={classes.tokenInfo}>
                  Token: <strong>{entries.token}</strong> | Queue Position:{' '}
                  <strong>#{entries.queuePosition}</strong> | Estimated Time:{' '}
                  <strong>{entries.estimatedWaitingTime ?? 0} min</strong>
                </Typography>
                <Typography
                  variant='body2'
                  style={{ marginTop: 8, opacity: 0.9 }}
                >
                  Patient: {entries.name} | Department:{' '}
                  {
                    DEPARTMENT_OPTIONS?.find(
                      (d) => d?.value === entries.department
                    )?.label
                  }
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Flow 2: Patient with Appointment - Shows only relevant card */}
        {detectedFlow === 'with-appointment' && recognizedPatient && (
          <Paper className={classes.section} elevation={3}>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <Typography variant='h6' className={classes.sectionTitle}>
                <PersonIcon /> Patient with Appointment
              </Typography>
              <Button variant='outlined' size='small' onClick={handleStartOver}>
                Start Over
              </Button>
            </Box>
            <Divider className={classes.divider} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {capturedPhoto && (
                  <Box style={{ textAlign: 'center' }}>
                    <img
                      src={capturedPhoto}
                      alt='Patient'
                      style={{
                        width: '100%',
                        maxWidth: 400,
                        borderRadius: 8,
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant='subtitle1'
                  style={{
                    fontWeight: 600,
                    marginBottom: 16,
                    color: '#4caf50',
                    fontSize: '1.2rem',
                  }}
                >
                  ✓ Patient Recognized
                </Typography>

                <Box className={classes.patientInfoBox}>
                  <Box className={classes.patientInfoRow}>
                    <Typography className={classes.patientInfoLabel}>
                      Patient ID:
                    </Typography>
                    <Typography className={classes.patientInfoValue}>
                      {recognizedPatient.id}
                    </Typography>
                  </Box>
                  <Box className={classes.patientInfoRow}>
                    <Typography className={classes.patientInfoLabel}>
                      Name:
                    </Typography>
                    <Typography className={classes.patientInfoValue}>
                      {recognizedPatient.name}
                    </Typography>
                  </Box>
                  <Box className={classes.patientInfoRow}>
                    <Typography className={classes.patientInfoLabel}>
                      Mobile:
                    </Typography>
                    <Typography className={classes.patientInfoValue}>
                      {recognizedPatient.mobile}
                    </Typography>
                  </Box>
                  <Box className={classes.patientInfoRow}>
                    <Typography className={classes.patientInfoLabel}>
                      Appointment:
                    </Typography>
                    <Typography className={classes.patientInfoValue}>
                      {recognizedPatient.appointmentDate} at{' '}
                      {recognizedPatient.appointmentTime}
                    </Typography>
                  </Box>
                  <Box className={classes.patientInfoRow}>
                    <Typography className={classes.patientInfoLabel}>
                      Doctor:
                    </Typography>
                    <Typography className={classes.patientInfoValue}>
                      {recognizedPatient.doctor}
                    </Typography>
                  </Box>
                  <Box className={classes.patientInfoRow}>
                    <Typography className={classes.patientInfoLabel}>
                      Department:
                    </Typography>
                    <Typography className={classes.patientInfoValue}>
                      {
                        DEPARTMENT_OPTIONS.find(
                          (d) => d.value === recognizedPatient.department
                        )?.label
                      }
                    </Typography>
                  </Box>
                </Box>

                {generatedToken1 && (
                  <Box className={classes.tokenBox} style={{ marginTop: 16 }}>
                    <Typography
                      variant='subtitle1'
                      className={classes.tokenTitle}
                    >
                      <CheckCircleIcon /> Token Generated
                    </Typography>
                    <Typography className={classes.tokenInfo}>
                      Token: <strong>{generatedToken1.token}</strong> | Queue
                      Position:{' '}
                      <strong>#{generatedToken1.queuePosition}</strong> |
                      Estimated Time:{' '}
                      <strong>
                        {generatedToken1.estimatedWaitingTime} min
                      </strong>
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Flow 3: Patient without Appointment (Walk-in) - Shows only relevant card */}
        {detectedFlow === 'without-appointment' && recognizedPatient && (
          <Paper className={classes.section} elevation={3}>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <Typography variant='h6' className={classes.sectionTitle}>
                <PersonIcon /> Patient without Appointment (Walk-in)
              </Typography>
              <Button variant='outlined' size='small' onClick={handleStartOver}>
                Start Over
              </Button>
            </Box>
            <Divider className={classes.divider} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {capturedPhoto && (
                  <Box style={{ textAlign: 'center' }}>
                    <img
                      src={capturedPhoto}
                      alt='Patient'
                      style={{
                        width: '100%',
                        maxWidth: 400,
                        borderRadius: 8,
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant='subtitle1'
                  style={{
                    fontWeight: 600,
                    marginBottom: 16,
                    color: '#4caf50',
                    fontSize: '1.2rem',
                  }}
                >
                  ✓ Patient Recognized
                </Typography>

                <Box className={classes.patientInfoBox}>
                  <Box className={classes.patientInfoRow}>
                    <Typography className={classes.patientInfoLabel}>
                      Patient ID:
                    </Typography>
                    <Typography className={classes.patientInfoValue}>
                      {recognizedPatient.id}
                    </Typography>
                  </Box>
                  <Box className={classes.patientInfoRow}>
                    <Typography className={classes.patientInfoLabel}>
                      Name:
                    </Typography>
                    <Typography className={classes.patientInfoValue}>
                      {recognizedPatient.name}
                    </Typography>
                  </Box>
                  <Box className={classes.patientInfoRow}>
                    <Typography className={classes.patientInfoLabel}>
                      Mobile:
                    </Typography>
                    <Typography className={classes.patientInfoValue}>
                      {recognizedPatient.mobile}
                    </Typography>
                  </Box>
                  <Box className={classes.patientInfoRow}>
                    <Typography className={classes.patientInfoLabel}>
                      Registered:
                    </Typography>
                    <Typography className={classes.patientInfoValue}>
                      {recognizedPatient.registrationDate}
                    </Typography>
                  </Box>
                </Box>

                <Divider style={{ margin: '20px 0' }} />

                {/* Visitor Relationship Selection */}
                <Box className={classes.visitorSelectionBox}>
                  <Typography
                    variant='subtitle2'
                    style={{
                      fontWeight: 600,
                      marginBottom: 8,
                      color: '#333',
                      textAlign: 'center',
                    }}
                  >
                    Who is visiting?
                  </Typography>
                  <Typography
                    variant='caption'
                    style={{
                      color: '#666',
                      textAlign: 'center',
                      display: 'block',
                      marginBottom: 16,
                    }}
                  >
                    Select whom you want to create visit for
                  </Typography>

                  <Box className={classes.visitorOptionsContainer}>
                    <Box
                      className={`${classes.visitorOption} ${
                        selectedVisitorRelationship === 'self'
                          ? classes.visitorOptionSelected
                          : ''
                      }`}
                      onClick={() => setSelectedVisitorRelationship('self')}
                    >
                      <FaceIcon
                        className={`${classes.visitorIcon} ${
                          selectedVisitorRelationship === 'self'
                            ? classes.visitorIconSelected
                            : ''
                        }`}
                      />
                      <Typography
                        className={`${classes.visitorLabel} ${
                          selectedVisitorRelationship === 'self'
                            ? classes.visitorLabelSelected
                            : ''
                        }`}
                      >
                        Self
                      </Typography>
                    </Box>

                    <Box
                      className={`${classes.visitorOption} ${
                        selectedVisitorRelationship === 'wife'
                          ? classes.visitorOptionSelected
                          : ''
                      }`}
                      onClick={() => setSelectedVisitorRelationship('wife')}
                    >
                      <WomanIcon
                        className={`${classes.visitorIcon} ${
                          selectedVisitorRelationship === 'wife'
                            ? classes.visitorIconSelected
                            : ''
                        }`}
                      />
                      <Typography
                        className={`${classes.visitorLabel} ${
                          selectedVisitorRelationship === 'wife'
                            ? classes.visitorLabelSelected
                            : ''
                        }`}
                      >
                        Wife
                      </Typography>
                    </Box>

                    <Box
                      className={`${classes.visitorOption} ${
                        selectedVisitorRelationship === 'child'
                          ? classes.visitorOptionSelected
                          : ''
                      }`}
                      onClick={() => setSelectedVisitorRelationship('child')}
                    >
                      <ChildCareIcon
                        className={`${classes.visitorIcon} ${
                          selectedVisitorRelationship === 'child'
                            ? classes.visitorIconSelected
                            : ''
                        }`}
                      />
                      <Typography
                        className={`${classes.visitorLabel} ${
                          selectedVisitorRelationship === 'child'
                            ? classes.visitorLabelSelected
                            : ''
                        }`}
                      >
                        Child
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider style={{ margin: '20px 0' }} />

                <Typography
                  variant='subtitle2'
                  style={{ fontWeight: 600, marginBottom: 16, color: '#333' }}
                >
                  Visit Details
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      required
                      variant='outlined'
                      size='small'
                    >
                      <InputLabel>Department</InputLabel>
                      <Select
                        value={selectedDepartment}
                        label='Department'
                        onChange={(e) => setSelectedDepartment(e.target.value)}
                      >
                        <MenuItem value=''>
                          <em>Select Department</em>
                        </MenuItem>
                        {DEPARTMENT_OPTIONS.map((dept) => (
                          <MenuItem key={dept.value} value={dept.value}>
                            {dept.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      required
                      variant='outlined'
                      size='small'
                    >
                      <InputLabel>Visit Purpose</InputLabel>
                      <Select
                        value={selectedVisitPurpose}
                        label='Visit Purpose'
                        onChange={(e) =>
                          setSelectedVisitPurpose(
                            e.target.value as VisitPurpose
                          )
                        }
                      >
                        <MenuItem value=''>
                          <em>Select Visit Purpose</em>
                        </MenuItem>
                        <MenuItem value='Appointment'>Consultation</MenuItem>
                        <MenuItem value='Lab test'>Lab Test</MenuItem>
                        <MenuItem value='Pharmacy'>Pharmacy</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant='contained'
                      fullWidth
                      onClick={handleGenerateToken2}
                      disabled={
                        !selectedVisitorRelationship ||
                        !selectedDepartment ||
                        !selectedVisitPurpose
                      }
                      className={classes.buttonSuccess}
                      startIcon={<CheckCircleIcon />}
                    >
                      Generate Token
                    </Button>
                  </Grid>
                </Grid>

                {generatedToken2 && (
                  <Box className={classes.tokenBox} style={{ marginTop: 16 }}>
                    <Typography
                      variant='subtitle1'
                      className={classes.tokenTitle}
                    >
                      <CheckCircleIcon /> Token Generated
                    </Typography>
                    <Typography className={classes.tokenInfo}>
                      Token: <strong>{generatedToken2.token}</strong> | Queue
                      Position:{' '}
                      <strong>#{generatedToken2.queuePosition}</strong> |
                      Estimated Time:{' '}
                      <strong>
                        {generatedToken2.estimatedWaitingTime} min
                      </strong>
                    </Typography>
                    <Typography
                      variant='body2'
                      style={{ marginTop: 8, opacity: 0.9 }}
                    >
                      Department:{' '}
                      {
                        DEPARTMENT_OPTIONS.find(
                          (d) => d.value === generatedToken2.department
                        )?.label
                      }{' '}
                      | Visit: {generatedToken2.visitPurpose}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Optional: Search Section (keeping for reference, hidden during photo flow) */}
        {!detectedFlow && false && (
          <Paper className={classes.section} elevation={3}>
            <Typography variant='h6' className={classes.sectionTitle}>
              🔍 Search Patient
            </Typography>
            <Divider className={classes.divider} />

            <div className={classes.searchContainer}>
              <Box className={classes.searchBox}>
                <ClickAwayListener onClickAway={handleClickAway}>
                  <div className={classes.searchInputWrapper}>
                    <TextField
                      ref={searchInputRef}
                      label='Search by Mobile, Token or Name'
                      value={searchQuery}
                      onChange={(e) => handleSearchQueryChange(e.target.value)}
                      fullWidth
                      variant='outlined'
                      placeholder='Type to search...'
                      size='small'
                      onFocus={() => {
                        if (searchQuery.trim()) {
                          setShowSearchDropdown(true)
                        }
                      }}
                    />
                    {showSearchDropdown && searchQuery?.trim() && (
                      <Paper className={classes.searchDropdown} elevation={3}>
                        <List>
                          <ListItem
                            className={classes.searchDropdownItem}
                            onClick={() => handleSearchTypeSelect('token')}
                          >
                            <BadgeIcon className={classes.searchDropdownIcon} />
                            <div className={classes.searchDropdownText}>
                              <Typography
                                className={classes.searchDropdownLabel}
                              >
                                Search Token Number for{' '}
                                <span className={classes.searchDropdownQuery}>
                                  {searchQuery}
                                </span>
                              </Typography>
                            </div>
                          </ListItem>
                          <ListItem
                            className={classes.searchDropdownItem}
                            onClick={() => handleSearchTypeSelect('mobile')}
                          >
                            <PhoneIcon className={classes.searchDropdownIcon} />
                            <div className={classes.searchDropdownText}>
                              <Typography
                                className={classes.searchDropdownLabel}
                              >
                                Search Mobile Number for{' '}
                                <span className={classes.searchDropdownQuery}>
                                  {searchQuery}
                                </span>
                              </Typography>
                            </div>
                          </ListItem>

                          <ListItem
                            className={classes.searchDropdownItem}
                            onClick={() => handleSearchTypeSelect('name')}
                          >
                            <PhoneIcon className={classes.searchDropdownIcon} />
                            <div className={classes.searchDropdownText}>
                              <Typography
                                className={classes.searchDropdownLabel}
                              >
                                Search Name for{' '}
                                <span className={classes.searchDropdownQuery}>
                                  {searchQuery}
                                </span>
                              </Typography>
                            </div>
                          </ListItem>
                        </List>
                      </Paper>
                    )}
                  </div>
                </ClickAwayListener>
              </Box>
            </div>

            <div className={classes.searchResults}>
              {!searchType && searchQuery?.trim() === '' ? (
                <Box className={classes.emptyState}>
                  <Typography variant='body1'>
                    Enter a search query and select search type
                  </Typography>
                </Box>
              ) : !searchType ? (
                <Box className={classes.emptyState}>
                  <Typography variant='body1'>
                    Please select a search type from the dropdown
                  </Typography>
                </Box>
              ) : results?.length === 0 ? (
                <Box className={classes.emptyState}>
                  <Typography variant='body1'>
                    No results found for &ldquo;{searchQuery}&rdquo;
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography variant='subtitle2' style={{ marginBottom: 16 }}>
                    Found {results?.length} result
                    {results?.length > 1 ? 's' : ''}
                  </Typography>
                  {results?.map((r) => (
                    <Card
                      key={r?.id}
                      className={classes.resultCard}
                      elevation={2}
                    >
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Box className={classes.resultRow}>
                              <Typography className={classes.resultLabel}>
                                Name:
                              </Typography>
                              <Typography className={classes.resultValue}>
                                {r?.name}
                              </Typography>
                            </Box>
                            <Box className={classes.resultRow}>
                              <Typography className={classes.resultLabel}>
                                Mobile:
                              </Typography>
                              <Typography className={classes.resultValue}>
                                {r?.mobile}
                              </Typography>
                            </Box>
                            <Box className={classes.resultRow}>
                              <Typography className={classes.resultLabel}>
                                Department:
                              </Typography>
                              <Typography className={classes.resultValue}>
                                {
                                  DEPARTMENT_OPTIONS?.find(
                                    (d) => d?.value === r?.department
                                  )?.label
                                }
                              </Typography>
                            </Box>
                            <Box className={classes.resultRow}>
                              <Typography className={classes.resultLabel}>
                                Estimated Time:
                              </Typography>
                              <Typography className={classes.resultValue}>
                                {r?.estimatedWaitingTime ?? 0} min
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box className={classes.resultRow}>
                              <Typography className={classes.resultLabel}>
                                Visit Purpose:
                              </Typography>
                              <Typography className={classes.resultValue}>
                                {r?.visitPurpose}
                              </Typography>
                            </Box>
                            <Box className={classes.resultRow}>
                              <Typography className={classes.resultLabel}>
                                Token Number:
                              </Typography>
                              <Typography
                                className={classes.resultValue}
                                style={{ fontWeight: 600, color: '#667eea' }}
                              >
                                {r?.token}
                              </Typography>
                            </Box>
                            <Box className={classes.resultRow}>
                              <Typography className={classes.resultLabel}>
                                Queue Position:
                              </Typography>
                              <Typography
                                className={classes.resultValue}
                                style={{ fontWeight: 600 }}
                              >
                                #{r?.queuePosition}
                              </Typography>
                            </Box>
                            {r?.appointmentType && (
                              <Box className={classes.resultRow}>
                                <Typography className={classes.resultLabel}>
                                  Appointment Type:
                                </Typography>
                                <Typography
                                  className={classes.resultValue}
                                  style={{
                                    color:
                                      r?.appointmentType === 'Emergency'
                                        ? '#f44336'
                                        : '#4caf50',
                                    fontWeight: 500,
                                  }}
                                >
                                  {r?.appointmentType}
                                </Typography>
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </>
              )}
            </div>
          </Paper>
        )}
      </div>
    </>
  )
}

export default SmartQueueSystem
